package com.writerslab

data class Strings(
    val aiNoCredits: String,
    /** «1 credit per {N}K characters» — N приходит из pricing. */
    val aiPricing: (Int) -> String,
    val aiPreviewPlotDesc: String,
    val aiPreviewPlotSample: String,
    val aiPreviewCharactersDesc: String,
    val aiPreviewCharactersSample: String,
    val aiPreviewRecommendationsDesc: String,
    val aiPreviewRecommendationsSample: String,
    val aiPreviewReviewDesc: String,
    val aiPreviewReviewSample: String,
    val aiPreviewStrengthsDesc: String,
    val aiPreviewStrengthsSample: String,
    val aiPreviewUnresolvedDesc: String,
    val aiPreviewUnresolvedSample: String,
    val aiPreviewWhatYouGet: String,
    val aiPreviewTimeHint: String,
    val aiRerun: (Int) -> String,
    val aiScore: String,
    val aiTitle: String,
    val aiStatusIdle: String,
    val aiStatusRunning: String,
    val aiStatusDone: String,
    val aiStatusError: String,
    /** Shown when an already-paid-for AI report won't load (server/network
     *  error, not a missing report). Must steer the user to reloading rather
     *  than to the rerun button, which would charge for a report they own. */
    val aiReportLoadFailed: String,
    /** Map of AI scoring question id → localized question text.
     *
     *  The backend ships stable English ids (e.g. `plot_inciting_event`,
     *  `chars_actions_match_description`); per-locale strings are
     *  resolved here so that question text matches the rest of the UI.
     *  Missing keys fall back to the raw id — visible signal to add a
     *  translation. */
    val aiScoringQuestions: Map<String, String>,
    /** Header for a scoring category: «8/12 «да» · 6.7/10». Numeric is
     *  null when no questions are applicable (skip the score part). */
    val aiCategoryStats: (yes: Int, applicable: Int, numeric: Double?) -> String,
    val aiUnlock: (Int) -> String,
    val aiUnlockFree: String,
    val aiUnlockFreeAfterSignIn: String,
    /** Sidebar variant of the launch button — narrower column, the
     *  block header «Глубокий анализ» already names the action, so the
     *  button drops «Получить глубокий разбор» and shows just the verb +
     *  price. Avoids two-line wrap inside the sidebar card. */
    val aiSidebarRun: (Int) -> String,
    val aiSidebarSignIn: String,
    val aiFreeShortBadge: (Int) -> String,
    val aiFreeShortBadgeAnon: String,
    val aiFreeShortHint: (Int) -> String,
    val aiFreeShortHintAnon: String,
    /** Quota line shown under the AI CTA regardless of the current file
     *  length — so users learn about the «short texts are free» rule
     *  before they bother uploading anything. Three flavours: anon (call
     *  to sign in), logged-in with quota left, logged-in with quota
     *  exhausted. The integer arg is the word-count threshold. */
    val aiFreeShortQuotaAnon: (Int) -> String,
    val aiFreeShortQuotaRemaining: (Int, Int) -> String,
    val aiFreeShortQuotaExhausted: (Int) -> String,
    val analysisFailed: String,
    val analyzing: (String) -> String,
    val appName: String,
    val arcPrefix: String,
    val change: String,
    val charactersLabel: String,
    val charsLabel: String,
    val close: String,
    val credits: String,
    /** Short label for the user's current credit balance inside the
     *  top-up dialog. Pairs with the dragon icon as «Баланс: N 🐉», so
     *  the word names the meaning and the icon labels the unit. */
    val balanceLabel: String,
    val detailedMetrics: String,
    val detailedMetricsExplain: String,
    // Framed result-block titles (shown on the block's collapsible header bar).
    val blockStatic: String,
    val blockManuscript: String,
    val blockAi: String,
    val blockPromo: String,
    // One-line subtitles under each block title — what the block does.
    val blockStaticSub: String,
    val blockManuscriptSub: String,
    val blockAiSub: String,
    val blockPromoSub: String,
    // ─── Block-kind badges — uppercase eyebrow that distinguishes
    //     statistics-only blocks from AI-driven blocks at a glance.
    val blockKindStatic: String,
    val blockKindAi: String,
    // ─── Per-block "?" help — short title + body per sidebar/main block.
    val helpQualityTitle: String,
    val helpQualityBody: String,
    val helpHighlightsTitle: String,
    val helpHighlightsBody: String,
    val helpAiScoreTitle: String,
    val helpAiScoreBody: String,
    val helpBookProfileTitle: String,
    val helpBookProfileBody: String,
    val helpPromoSettingsTitle: String,
    val helpPromoSettingsBody: String,
    val helpMetricsTitle: String,
    val helpMetricsBody: String,
    val helpPromoMaterialsTitle: String,
    val helpPromoMaterialsBody: String,
    val helpAiInsightsTitle: String,
    val helpAiInsightsBody: String,
    val eulaPrefix: String,
    val eulaLink: String,
    val privacyLink: String,
    val metaTitle: String,
    val metaDescription: String,
    val metaLocale: String,
    val packsHeading: String,
    val packStartName: String,
    val packMediumName: String,
    val packLargeName: String,
    val packPerBook: String,
    val packDiscount: (Int) -> String,
    val packCreditsCount: (Int) -> String,
    val packCheckoutStub: String,
    /** Toast after the user lands back from the hosted checkout page
     *  with `?payment=<id>` and the backend confirms credit grant.
     *  Takes the number of credits added. */
    val paymentSuccess: (Int) -> String,
    /** Toast when the user explicitly cancelled / declined the payment
     *  on the hosted page (or the provider returned CANCELED). */
    val paymentCanceled: String,
    /** Under the pack cards while the tab is leaving for the payment page. */
    val checkoutRedirecting: String,
    /** Banner while a payment the user left for hasn't settled yet. */
    val paymentWaitingBanner: String,
    /** Banner button: reopen the same, still unpaid invoice. */
    val paymentResume: String,
    /** Toast when /checkout itself fails (provider down, misconfigured,
     *  network). Generic — covers 502/503 and exceptions. */
    val paymentError: String,
    val regionRussia: String,
    val regionInternational: String,
    val weakMetricsHint: String,
    val supportContact: String,
    val supportEmailSubject: String,
    val supportEmailBody: String,
    val emotionAnger: String,
    val emotionAnticipation: String,
    val emotionDisgust: String,
    val emotionFear: String,
    val emotionJoy: String,
    val emotionSadness: String,
    val emotionSurprise: String,
    val emotionTrust: String,
    val footerHint: String,
    val footerPowered: String,
    val heroesMany: String,
    val heroesNone: String,
    val heroesOne: String,
    val heroesSeveral: String,
    val heroesVeryMany: String,
    val keyStrengths: String,
    /** Section heading: «Weaknesses» — used opposite keyStrengths /
     *  strengths so the beta-reader tab surfaces what's not working,
     *  not just what is. */
    val keyWeaknesses: String,
    val weaknesses: String,
    val promoSettingsLabel: String,
    val replaceBook: String,
    val unresolvedEmptyTitle: String,
    val unresolvedEmptyHint: String,
    val weakSpots: String,
    val readingTimeLabel: String,
    val hoursShort: String,
    val minutesShort: String,
    val landingExampleAnnotationHook: String,
    val landingExampleAnnotationLabel: String,
    val landingExampleAnnotationText: String,
    val landingExampleReviewLabel: String,
    val landingExampleReviewText: String,
    val landingHeadline: String,
    val landingSubheadline: String,
    // "What you get" section: umbrella title + two columns (text work / publishing
    // prep), each a list of (feature label → short benefit line).
    val landingFeaturesTitle: String,
    val landingFeaturesTextTitle: String,
    val landingFeaturesTextItems: List<Pair<String, String>>,
    val landingFeaturesPubTitle: String,
    val landingFeaturesPubItems: List<Pair<String, String>>,
    val landingFaqTitle: String,
    /** FAQ shown at the foot of the main-page landing: question → markdown
     *  answer (paragraphs / bold / bullet lists), rendered via MarkdownBlock. */
    val landingFaq: List<Pair<String, String>>,
    // Manuscript reader panel (TipTap) shown under the AI analysis.
    val manuscriptTitle: String,
    val manuscriptHint: String,
    // Highlight legend — group labels double as the hover-hint text.
    val hlGroupCliche: String,
    val hlGroupFilter: String,
    val hlGroupBureaucratese: String,
    val hlGroupFiller: String,
    val hlGroupBegan: String,
    val hlGroupTautology: String,
    val hlGroupWeakSpeech: String,
    val hlGroupAdjStack: String,
    val hlGroupAuthorStance: String,
    val hlGroupIntensifier: String,
    val hlGroupWeakVerb: String,
    val hlGroupPassive: String,
    // Per-group hover hints: why it's an issue + how to fix it.
    val hlHintCliche: String,
    val hlHintFilter: String,
    val hlHintFiller: String,
    val hlHintBureaucratese: String,
    val hlHintBegan: String,
    val hlHintTautology: String,
    val hlHintWeakSpeech: String,
    val hlHintAdjStack: String,
    val hlHintAuthorStance: String,
    val hlHintIntensifier: String,
    val hlHintWeakVerb: String,
    val hlHintPassive: String,
    val hlPrevIssue: String,
    val hlNextIssue: String,
    val hlModeStatic: String,
    val hlAiIntro: String,
    val hlAiLoading: String,
    val hlAiError: String,
    val hlAiEmpty: String,
    /** Progress note while the async full-manuscript scan runs (minutes). */
    val hlAiFullLoading: String,
    /** Whole-manuscript scan running as a batch (minutes to an hour); takes the start time "H:MM". */
    val hlAiBatchNote: (String) -> String,
    /** Preview button label; arg = free previews left (null = unknown). */
    val hlAiPreview: (Int?) -> String,
    /** Full-scan button label; arg = pre-formatted credit cost. */
    val hlAiFull: (String) -> String,
    /** Note above the run buttons, anonymous users only: sign in to run. */
    val hlAiSignInToRun: String,
    /** Full-scan rate note, part before the credit icon; arg = credit cost
     *  per 100k chars (derived from the same calc as the real charge). */
    val hlAiFullRate: (String) -> String,
    /** Full-scan rate note, part after the credit icon (the unit). */
    val hlAiFullRateUnit: String,
    val hlScrollHintMouse: String,
    val hlScrollHintTouch: String,
    val manuscriptLoading: String,
    val manuscriptUnavailable: String,
    // Editing the text in the reader.
    val msEditLockedHint: String,
    val msSave: String,
    val msSaving: String,
    val msCancelEdit: String,
    val msCancelConfirm: String,
    /** Arg: paragraphs changed so far. */
    val msEditedCount: (Int) -> String,
    /** Arg: smart marks whose words are gone from the edited places. */
    val msSaved: (Int) -> String,
    val msSavedReloadPage: String,
    val msSaveTextChanged: String,
    val msSaveScanRunning: String,
    val msSaveBusy: String,
    /** Arg: words left. */
    val msSaveTooShort: (Int) -> String,
    val msSaveFailed: String,
    /** Arg: formatted date of the last edit. */
    val msEditedAt: (String) -> String,
    val msDownloadTxt: String,
    val msUncheckedNote: String,
    val msDraftFound: String,
    /** The draft was made on an older text (another tab saved since). */
    val msDraftFoundStale: String,
    val msDraftRestore: String,
    val msDraftDiscard: String,
    /** Phones: leave the full-screen text. */
    val msDone: String,
    val msRecheck: String,
    val msRechecking: String,
    val msRecheckFree: String,
    val msRecheckFreeSlot: String,
    /** Button suffix + click note: no smart scan to re-check against yet. */
    val msRecheckNeedsScan: String,
    val msRecheckNeedsScanNote: String,
    /** Button suffix + click note: nothing changed since the smart scan. */
    val msRecheckNoChanges: String,
    val msRecheckNoChangesNote: String,
    val msRecheckPriceLoading: String,
    /** Args: findings removed, findings added in the re-checked places. */
    val msRechecked: (Int, Int) -> String,
    val msRecheckPriceChanged: String,
    val msRecheckFailed: String,
    val msRecheckRunning: String,
    // The «what editing gives» card for those who haven't bought yet.
    val msLockTitle: String,
    val msLockFix: String,
    val msLockRecheck: String,
    val msLockDownload: String,
    /** Arg: the cheapest pack's price, formatted. */
    val msLockPrice: (String) -> String,
    val msLockChoosePack: String,
    val msLockSignIn: String,
    val msLockLater: String,
    /** Deep-analysis report made before the text was edited; arg: formatted date of the edit or null. */
    val aiTextChanged: (String?) -> String,
    val loadingMessages: List<String>,
    val metricArcVolatility: String,
    val metricAuthorStance: String,
    val metricCefrRange: String,
    val metricCleanSyntax: String,
    val metricCoherence: String,
    val metricComplexity: String,
    val metricConcreteness: String,
    val metricDialogue: String,
    val metricDirectPov: String,
    val metricDirectness: String,
    val metricExpressiveness: String,
    val metricFillers: String,
    val metricLiveliness: String,
    val metricMorality: String,
    val metricNoNominalisation: String,
    val metricNoRepetition: String,
    val metricOriginality: String,
    val metricPace: String,
    val metricPathos: String,
    val metricPhrasalVerbs: String,
    val metricPrecision: String,
    val metricRareVocabulary: String,
    val metricRegisterShift: String,
    val metricRhythm: String,
    val metricSensory: String,
    val metricStageDirections: String,
    val metricVerbStrength: String,
    val metricVividness: String,
    val metricVocabulary: String,
    val metricVocabularyAge: String,
    val metricVoices: String,
    val moodBright: String,
    val moodDark: String,
    val moodHeavy: String,
    val moodLabel: String,
    val moodNeutral: String,
    val moodRatherBright: String,
    val moodRatherDark: String,
    val povFirst: String,
    val povLabel: String,
    val povMixed: String,
    val povThird: String,
    val povUnknown: String,
    val previousBooks: String,
    val historyEmptyTitle: String,
    val historyEmptyHint: String,
    // Book-history row badges: deep-analysis done / smart-highlighting done.
    // No "AI"/"ИИ" wording in the UI (see feedback_no_ai_term_in_ui).
    // Label = what the user sees on the pill; Tip = hover explanation.
    val badgeDeep: String,
    val badgeSmart: String,
    val badgeDeepTip: String,
    val badgeSmartTip: String,
    val retryButton: String,
    val menuAria: String,
    val qualityHint: String,
    val qualityCaveat: String,
    val redeemApply: String,
    val redeemCodeHint: String,
    val redeemErrorFormat: String,
    val redeemErrorNetwork: String,
    val redeemErrorUsed: String,
    val redeemSuccess: (Int) -> String,
    val relationships: String,
    val sectionNarrative: String,
    val sectionNarrativeHint: String,
    val sectionProfile: String,
    val sectionProfileHint: String,
    val sectionStyle: String,
    val sectionStyleHint: String,
    val sectionStyleHintUnreliable: String,
    val shortTextWarningModerate: String,
    val shortTextWarningSevere: String,
    val signIn: String,
    val signOut: String,
    val storyAndCharacters: String,
    val strengths: String,
    val tabPlot: String,
    /** Heading shown above the character-questions subsection on the
     *  Characters tab — separates the «who's in this book» list from
     *  the «how well are they drawn» assessment. */
    val characterAssessment: String,
    // Plot-aspect labels (Plot tab):
    val plotStructure: String,
    val plotPacing: String,
    val plotConflict: String,
    val plotStakes: String,
    val plotCausality: String,
    // Character-assessment aspect labels (Characters tab):
    val charConsistency: String,
    val charDepth: String,
    val charMotivation: String,
    val charDistinctness: String,
    val charArc: String,
    val tabLanguage: String,
    val aiPreviewLanguageDesc: String,
    val aiPreviewLanguageSample: String,
    val languageSentenceCraft: String,
    val languageVocabulary: String,
    val languageClarity: String,
    val languageImagery: String,
    val languageRegister: String,
    val tabCharacters: String,
    val tabRecommendations: String,
    val tabReview: String,
    val tabStrengths: String,
    val tabUnresolved: String,
    val promoMaterialsTitle: String,
    val promoMaterialsHintReady: String,
    val promoMaterialsHintLocked: String,
    val promoAnnotationTitle: String,
    val promoCoverTitle: String,
    val promoCoverTitleLabel: String,
    val promoCoverAuthorLabel: String,
    val promoCoverVariantPrefix: String,
    val promoCoverGenerateAll: String,
    val promoCoverRegenerateAll: String,
    val promoCoverRegenerateOne: String,
    val promoCoverDownloadOne: String,
    val promoCoverDownloadAll: String,
    val promoCoverTitleRequired: String,
    val promoCoverTextModeLabel: String,
    val promoCoverTextModeOverlay: String,
    val promoCoverTextModeBaked: String,
    val promoVideoTitle: String,
    val promoGenerateButton: String,
    val promoRegenerateButton: String,
    val promoGeneratingLabel: String,
    // ─── Teaser two-step UX ────────────────────────────────
    /** Кнопка первого шага: подобрать кадр + сцену (~30с). */
    val promoTeaserPreviewButton: String,
    /** Кнопка «попробовать другую сцену» — снова шаг 1. */
    val promoTeaserPickAnotherScene: String,
    /** Кнопка второго шага: запустить i2v-рендер (~1-2 мин ожидания). */
    val promoTeaserRenderVideoButton: String,
    /** Кнопка «скачать готовое видео». */
    val promoTeaserDownloadButton: String,
    /** Loading copy при генерации кадра-превью. */
    val promoTeaserLoadingPreview: String,
    /** Loading copy при рендере видео. */
    val promoTeaserLoadingVideo: String,
    /** Loading copy при перегенерации сцены. */
    val promoTeaserLoadingRegenerate: String,
    /** Подпись метаданных: выбранная сцена. */
    val promoTeaserSceneLabel: String,
    /** Ошибка: Kling зарубил видео из-за copyright-фильтра. */
    val promoTeaserErrorCopyright: String,
    /** Ошибка: видео-модель отказалась анимировать кадр с
     *  распознаваемым лицом (privacy-фильтр на input image). */
    val promoTeaserErrorPrivacyFace: String,
    val promoAnnotationPlaceholderHeading: String,
    val promoAnnotationPlaceholderBody: String,
    val promoAnnotationDownloadButton: String,
    /** «· N кр» / «· N cr» — суффикс стоимости для кнопок-генераций. */
    val priceTag: (Int) -> String,
    /** «N кр» / «N cr» — bare-form для табличной разбивки цен. */
    val priceUnits: (Int) -> String,
    /** Заголовок двухэтапного explainer-блока в idle-состоянии тизер-вкладки. */
    val promoTeaserStepsHeading: String,
    val promoTeaserStep1Title: String,
    val promoTeaserStep1Time: String,
    /** Цена шага 1 с учётом free-quota: (freeQuota, unitCost) → строка. */
    val promoTeaserStep1Cost: (Int, Int) -> String,
    /** Free-quota hint для аннотации: (remaining, total) → строка. */
    val freeQuotaAnnotationRemaining: (Int, Int) -> String,
    /** Hint когда квота на аннотации исчерпана: (total) → строка. */
    val freeQuotaAnnotationExhausted: (Int) -> String,
    /** Free-quota hint для обложек: (remaining, total) → строка. */
    val freeQuotaCoverRemaining: (Int, Int) -> String,
    /** Hint когда квота на обложки исчерпана: (total) → строка. */
    val freeQuotaCoverExhausted: (Int) -> String,
    /** Free-quota hint для тизер-превью: (remaining, total) → строка. */
    val freeQuotaTeaserRemaining: (Int, Int) -> String,
    /** Hint когда квота на тизер-превью исчерпана: (total) → строка. */
    val freeQuotaTeaserExhausted: (Int) -> String,
    /** Hint под кнопкой превью-подсветки: (remaining, total) → строка. */
    val freeQuotaHighlightRemaining: (Int, Int) -> String,
    /** Hint когда бесплатные превью-подсветки исчерпаны: (total) → строка. */
    val freeQuotaHighlightExhausted: (Int) -> String,
    val promoTeaserStep2Title: String,
    val promoTeaserStep2Time: String,
    val promoTeaserStepsTotalLabel: String,
    val promoHookVariantsLabel: String,
    val promoStatusReady: String,
    val promoStatusLocked: String,
    val tag19thCentury: String,
    val tagAcademy: String,
    val tagAction: String,
    val tagAntiquity: String,
    val tagApocalypse: String,
    val tagAscension: String,
    val tagCastlePalace: String,
    val tagChosenOne: String,
    val tagComingOfAge: String,
    val tagCozy: String,
    val tagCyberpunk: String,
    val tagDark: String,
    val tagDarkLord: String,
    val tagDemons: String,
    val tagDetective: String,
    val tagDragons: String,
    val tagDystopia: String,
    val tagElves: String,
    val tagEnemiesToLovers: String,
    val tagFakeMarriage: String,
    val tagFantasy: String,
    val tagForbiddenLove: String,
    val tagFuture: String,
    val tagHeist: String,
    val tagInvestigation: String,
    val tagIronic: String,
    val tagIsekai: String,
    val tagIsekaiFemale: String,
    val tagLitrpg: String,
    val tagLoneliness: String,
    val tagLossGrief: String,
    val tagMagic: String,
    val tagMedieval: String,
    val tagMercenary: String,
    val tagNostalgic: String,
    val tagPoliticsIntrigue: String,
    val tagPostApocalypse: String,
    val tagPowerTyranny: String,
    val tagPrison: String,
    val tagRedemption: String,
    val tagRevenge: String,
    val tagRomance: String,
    val tagSchoolAcademy: String,
    val tagSciFi: String,
    val tagSeaShip: String,
    val tagSecondChance: String,
    val tagSpace: String,
    val tagSport: String,
    val tagSpy: String,
    val tagSteampunk: String,
    val tagThriller: String,
    val tagTournament: String,
    val tagUssr: String,
    val tagVampires: String,
    val tagWar: String,
    val tagWerewolves: String,
    val tagWw2: String,
    val tagZombies: String,
    val textQuality: String,
    val tipArcVolatility: String,
    val tipAuthorStance: String,
    val tipCefrRange: String,
    val tipCleanSyntax: String,
    val tipCoherence: String,
    val tipComplexity: String,
    val tipConcreteness: String,
    val tipDialogue: String,
    val tipDirectPov: String,
    val tipDirectness: String,
    val tipExpressiveness: String,
    val tipFillers: String,
    val tipLiveliness: String,
    val tipMorality: String,
    val tipNoNominalisation: String,
    val tipNoRepetition: String,
    val tipOriginality: String,
    val tipPace: String,
    val tipPathos: String,
    val tipPhrasalVerbs: String,
    val tipPrecision: String,
    val tipPrefix: String,
    val tipRareVocabulary: String,
    val tipRegisterShift: String,
    val tipRhythm: String,
    val tipSensory: String,
    val tipStageDirections: String,
    val tipVerbStrength: String,
    val tipVividness: String,
    val tipVocabulary: String,
    val tipVocabularyAge: String,
    val tipVoices: String,
    val topUpDebug: String,
    val topUpTitle: String,
    // ─── Signup-bonus banner (anonymous, after upload) ─────────────────
    /** Prominent banner title inviting an anonymous user to sign in. */
    val signupBonusTitle: String,
    /** Free-generation list shown after the "N 🐉" credit count; args =
     *  free annotations / cover sets / text checks, all from Pricing so the
     *  copy tracks the real quotas. Teasers are omitted on purpose — only the
     *  scene preview is free, not the video render, so listing them would
     *  overpromise. */
    val signupBonusBody: (Int, Int, Int) -> String,
    // ─── Email verification ────────────────────────────────────────────
    val verificationBannerText: (email: String) -> String,
    val verificationRequired: String,
    val verificationResend: String,
    val verificationConfirm: String,
    val verificationEmailSent: String,
    val verificationEmailError: String,
    val verificationCooldown: (seconds: Int) -> String,
    val verificationCooldownButton: (seconds: Int) -> String,
    val verificationNotYet: String,
    val verificationAlreadyDone: String,
    val unsupportedLanguageMessage: String,
    val unsupportedLanguageSampleHeader: String,
    val uploadFormats: String,
    val uploadPrivacy: String,
    /** «Single coherent work, not a collection» — surfaces the
     *  scope limit at upload time so users don't feed in a short-
     *  story anthology and get confused by mixed metrics. */
    val uploadScope: String,
    val uploadTitle: String,
    val wherePrefix: String,
    val words: String,
    val wordsAndChange: String,
)
